/* Trucker Party Hats — cart store (Shopify Ajax cart wrapper).
   Single source of truth that talks to /cart.js. Exposed as window.TPHCart.
   Subscribers get called on every change. State shape mirrors Shopify's cart
   object — items have { id, key, quantity, price, line_price, image,
   product_title, variant_title, url, options_with_values }.

   API stays similar to the static-site version so existing UI keeps working:
     add({ id, quantity })   POST /cart/add.js
     setQty(key, n)          POST /cart/change.js
     remove(key)             setQty(key, 0)
     clear()                 POST /cart/clear.js
     subscribe(fn)
     count()                 cart.item_count
     subtotal()              dollars (items_subtotal_price / 100)
     comparedTotal()
     savings()
     freeShipQty() / qualifiesForFreeShipping()
*/
(() => {
  const FREE_SHIP_QTY = 3;
  const subs = new Set();

  let state = { items: [], item_count: 0, items_subtotal_price: 0 };

  async function refresh() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      state = await res.json();
      emit();
    } catch (err) { console.error('[cart] refresh failed:', err); }
  }

  function emit() {
    subs.forEach((fn) => { try { fn(state); } catch (e) { console.error(e); } });
  }

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    return res.json();
  }

  const store = {
    get: () => state,

    async add(item) {
      const id = item.id || item.variantId || item.variant_id;
      if (!id) { console.error('[cart] add: missing variant id', item); return; }
      const quantity = Math.max(1, item.quantity || 1);
      try {
        await postJSON('/cart/add.js', { id, quantity });
        await refresh();
      } catch (err) { console.error('[cart] add failed:', err); }
    },

    async setQty(key, qty) {
      const n = Math.max(0, Math.floor(qty));
      try {
        await postJSON('/cart/change.js', { id: key, quantity: n });
        await refresh();
      } catch (err) { console.error('[cart] setQty failed:', err); }
    },

    remove(key) { return store.setQty(key, 0); },

    async clear() {
      try {
        await postJSON('/cart/clear.js', {});
        await refresh();
      } catch (err) { console.error('[cart] clear failed:', err); }
    },

    subscribe(fn) {
      subs.add(fn);
      try { fn(state); } catch (e) { console.error(e); }
      return () => subs.delete(fn);
    },

    count: () => state.item_count || 0,
    subtotal: () => (state.items_subtotal_price || state.total_price || 0) / 100,
    comparedTotal() {
      return (state.items || []).reduce((acc, it) => {
        const compareAt = it.compare_at_price ?? it.original_price ?? it.price;
        return acc + (compareAt * it.quantity) / 100;
      }, 0);
    },
    savings() { return Math.max(0, store.comparedTotal() - store.subtotal()); },
    freeShipQty: () => FREE_SHIP_QTY,
    qualifiesForFreeShipping: () => store.count() >= FREE_SHIP_QTY,
  };

  window.TPHCart = store;
  refresh();
})();
